import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, CreditCard, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getPricing, createCheckoutSession, erpPrograms, trainingTracks } from '@/lib/api';

export default function PricingPage() {
  const [pricing, setPricing] = useState(null);
  const [selectedERP, setSelectedERP] = useState('ifs');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await getPricing();
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const handleEnroll = (erp, track, price) => {
    setSelectedProgram({ erp, track, price });
    setCheckoutOpen(true);
  };

  const handleCheckout = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await createCheckoutSession({
        erp_program: selectedProgram.erp,
        training_track: selectedProgram.track,
        customer_email: customerInfo.email,
        customer_name: customerInfo.name,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create checkout session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTrackName = (track) => {
    return track.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen pt-24" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <div
              
              
            >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">
                Transparent Pricing
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">
                Invest in Your ERP Career
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Choose the ERP platform and training track that matches your career goals. 
                All programs include live training, hands-on labs, and certification prep.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-20 lg:py-28">
        <div className="container-main">
          {/* ERP Selection Tabs */}
          <Tabs defaultValue="ifs" value={selectedERP} onValueChange={setSelectedERP} className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-12 bg-transparent">
              {Object.entries(erpPrograms).map(([key, program]) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="px-6 py-3 data-[state=active]:bg-brand-700 data-[state=active]:text-white rounded-full border border-slate-200 data-[state=active]:border-brand-700"
                  data-testid={`pricing-tab-${key}`}
                >
                  {program.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(erpPrograms).map(([erpKey, program]) => (
              <TabsContent key={erpKey} value={erpKey}>
                <div
                  
                  
                >
                  <div className="text-center mb-12">
                    <h2 className="text-2xl font-bold text-brand-900 mb-2">{program.fullName} Training</h2>
                    <p className="text-slate-600">{program.tagline}</p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                    {Object.entries(trainingTracks).map(([trackKey, track], index) => {
                      const price = pricing?.[erpKey]?.[trackKey] || 0;
                      const isPopular = trackKey === 'professional';

                      return (
                        <div
                          key={trackKey}
                          
                          
                          
                        >
                          <Card className={`program-card h-full relative overflow-hidden ${isPopular ? 'ring-2 ring-accent' : ''}`} data-testid={`pricing-card-${trackKey}`}>
                            {isPopular && (
                              <div className="absolute top-0 right-0 bg-accent text-white px-4 py-1 text-xs font-medium rounded-bl-lg">
                                Most Popular
                              </div>
                            )}
                            <CardHeader className="pb-4">
                              <span className="text-3xl mb-2">{track.icon}</span>
                              <CardTitle className="text-xl">{track.name}</CardTitle>
                              <CardDescription>{track.subtitle}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="mb-6">
                                <span className="text-4xl font-bold text-brand-900 font-mono">${price.toLocaleString()}</span>
                                <span className="text-slate-500 ml-2">USD</span>
                              </div>
                              
                              <p className="text-sm text-slate-500 mb-4">Duration: {track.duration}</p>
                              
                              <ul className="space-y-3 mb-8">
                                {track.features.map((feature, i) => (
                                  <li key={i} className="flex items-start space-x-2 text-sm">
                                    <Check className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-slate-600">{feature}</span>
                                  </li>
                                ))}
                              </ul>
                              
                              <Button 
                                className={isPopular ? 'btn-primary w-full' : 'btn-secondary w-full'}
                                onClick={() => handleEnroll(erpKey, trackKey, price)}
                                data-testid={`enroll-btn-${erpKey}-${trackKey}`}
                              >
                                Enroll Now
                              </Button>
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">All Programs Include</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              'Live Instructor-Led Sessions',
              'Hands-On ERP Lab Access',
              'Course Materials & Guides',
              'Session Recordings',
              'Certification Exam Prep',
              'Mock Tests & Practice',
              'Career Support',
              'Alumni Network Access'
            ].map((item, index) => (
              <div
                key={item}
                
                
                
                
                className="flex items-center space-x-3 bg-white p-4 rounded-lg"
              >
                <Check className="w-5 h-5 text-brand-500 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-brand-900 mb-6">Payment Options</h2>
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex items-center space-x-4">
                    <CreditCard className="w-8 h-8 text-brand-600" />
                    <div>
                      <h3 className="font-semibold text-brand-900">Credit/Debit Card</h3>
                      <p className="text-sm text-slate-600">Visa, Mastercard, Amex - Processed via Stripe</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white font-bold text-xs">PP</div>
                    <div>
                      <h3 className="font-semibold text-brand-900">PayPal</h3>
                      <p className="text-sm text-slate-600">Pay securely with your PayPal account</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center space-x-4">
                    <Building2 className="w-8 h-8 text-brand-600" />
                    <div>
                      <h3 className="font-semibold text-brand-900">Installment Plans</h3>
                      <p className="text-sm text-slate-600">Split payment into 2-3 installments (contact us)</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            
            <div className="bg-brand-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-brand-900 mb-4">Corporate Training</h2>
              <p className="text-slate-600 mb-6">
                Looking to train your team? We offer customized corporate training packages 
                with volume discounts and flexible scheduling.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center space-x-2 text-slate-700">
                  <Check className="w-4 h-4 text-brand-500" />
                  <span>Custom curriculum based on your needs</span>
                </li>
                <li className="flex items-center space-x-2 text-slate-700">
                  <Check className="w-4 h-4 text-brand-500" />
                  <span>Dedicated batches for your organization</span>
                </li>
                <li className="flex items-center space-x-2 text-slate-700">
                  <Check className="w-4 h-4 text-brand-500" />
                  <span>Volume discounts available</span>
                </li>
                <li className="flex items-center space-x-2 text-slate-700">
                  <Check className="w-4 h-4 text-brand-500" />
                  <span>Progress reporting for managers</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button className="btn-primary" data-testid="corporate-inquiry-btn">
                  Inquire for Corporate Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ-like section */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="section-heading mb-6">Have Questions?</h2>
            <p className="text-lg text-slate-600 mb-8">
              Not sure which program is right for you? Our advisors can help you choose 
              the best ERP and track based on your background and career goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="schedule-call-btn">
                  Schedule a Call
                </Button>
              </Link>
              <Link to="/webinar">
                <Button variant="outline" className="btn-secondary">
                  Attend Free Webinar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Your Enrollment</DialogTitle>
            <DialogDescription>
              {selectedProgram && (
                <>
                  {erpPrograms[selectedProgram.erp]?.fullName} - {formatTrackName(selectedProgram.track)}
                  <br />
                  <span className="font-mono font-bold text-lg text-brand-700">${selectedProgram.price?.toLocaleString()} USD</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input 
                id="name" 
                placeholder="John Doe"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                data-testid="checkout-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input 
                id="email" 
                type="email"
                placeholder="john@example.com"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                data-testid="checkout-email-input"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setCheckoutOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleCheckout}
              disabled={loading}
              data-testid="proceed-payment-btn"
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
